import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  IShipmentByStatusItem,
  IShipmentGroupedByOrder,
  SHIPMENT_COLUMN_STATUS_ID,
  SLA_STATUS
} from 'types/orders';
import { formatShortDate } from 'utils/formatDatesAndHours';
import { groupShipmentsByDestinationAndStatus, isCountryNameMatching } from '../../trackingOrder.utils';
import useOrderComments from 'hooks/useOrderComments';
import { bordColorTheme, nudosBlue } from 'styles/styles.constants';

const MAX_VISIBLE_SHIPMENTS = 3;
const MAX_EXPANDABLE_SHIPMENTS = 5;

interface IUseOperationsCardParams {
  data: IShipmentGroupedByOrder;
  onRefetch?: () => void;
  onFocusOrder?: (orderId: number | null) => void;
  focusedOrderId?: number | null;
  onCountryClick?: (countryId: number | null, countryName?: string) => void;
  focusedCountryNames?: string[];
  onOrganizationClick?: (organizationName: string | null) => void;
  focusedOrganizationName?: string | null;
}

interface IChecklistCounters {
  total: number;
  completed: number;
}

/**
 * Hook personalizado que encapsula toda la lógica de estados y handlers
 * para el componente OperationsCard.
 * @param params - Parámetros del hook incluyendo datos, callbacks y estado de focus global
 * @returns Objeto con estados, handlers y valores derivados para el componente
 */
const useOperationsCard = ({
  data,
  onRefetch,
  onFocusOrder,
  focusedOrderId,
  onCountryClick,
  focusedCountryNames,
  onOrganizationClick,
  focusedOrganizationName
}: IUseOperationsCardParams) => {
  const [isShipmentsExpanded, setIsShipmentsExpanded] = useState(false);
  const [isShipmentDrawerOpen, setIsShipmentDrawerOpen] = useState(false);
  const [isPreparationDrawerOpen, setIsPreparationDrawerOpen] = useState(false);
  const [selectedShipmentIds, setSelectedShipmentIds] = useState<number[]>([]);
  const [selectedShipmentIndex, setSelectedShipmentIndex] = useState(0);
  const [localChecklistCounters, setLocalChecklistCounters] = useState<Record<number, IChecklistCounters>>({});

  const {
    orderId,
    organizationName,
    destinationCountry,
    shipments: allShipmentsData = [],
    statusId,
    slaStatus,
    slaDeadline
  } = data;

  const previousShipmentIdsRef = useRef<string | null>(null)
  const currentShipmentIdsKey = useMemo(
    () => allShipmentsData.map(s => s.id).sort((a, b) => a - b).join(','),
    [allShipmentsData]
  );

  useEffect(() => {
    if (isPreparationDrawerOpen) return;

    const shipmentIdsChanged = previousShipmentIdsRef.current !== null && previousShipmentIdsRef.current !== currentShipmentIdsKey;
    const hasLocalCounters = Object.keys(localChecklistCounters).length > 0;

    if (shipmentIdsChanged && hasLocalCounters) setLocalChecklistCounters({});
    previousShipmentIdsRef.current = currentShipmentIdsKey;
  }, [currentShipmentIdsKey, isPreparationDrawerOpen, localChecklistCounters]);

  const isFocused = focusedOrderId === orderId;

  const {
    showCommentsModal,
    isLoadingComments,
    orderComments,
    handleOpenOrCloseCommentsModal,
    handleCommentsSubmission
  } = useOrderComments({ orderId });

  const orderNumber = orderId?.toString();
  const companyName = organizationName || 'Sin nombre';
  const countryFlagUrl = destinationCountry?.flag || '';
  const countryName = destinationCountry?.name || '';
  const countryId = destinationCountry?.id;
  const isCountryFocused =
    (focusedCountryNames?.some(selected => isCountryNameMatching(countryName, selected)) ?? false);
  const countryFocusIconColor = isCountryFocused ? nudosBlue : bordColorTheme.primary.fgDefault;
  const isOrganizationFocused = focusedOrganizationName === organizationName;
  const shipmentsSummary = allShipmentsData[0]?.order?.shipmentsSummary;
  const shipmentsCount = shipmentsSummary?.total ?? allShipmentsData.length;
  const shipmentGroups = groupShipmentsByDestinationAndStatus(allShipmentsData);
  const shipmentsInCardCount = shipmentGroups.length;
  const hasMixedStatuses = shipmentsSummary?.hasMixedStatuses ?? false;
  const showFocusButton = shipmentsCount > 1 && hasMixedStatuses;
  const isCancelled = statusId === SHIPMENT_COLUMN_STATUS_ID.CANCELLED;
  const isDelivered = statusId === SHIPMENT_COLUMN_STATUS_ID.DELIVERED;

  const isDelayed =
    !isCancelled &&
    !isDelivered &&
    (slaStatus === SLA_STATUS.WARNING_24H || slaStatus === SLA_STATUS.OVERDUE);

  const isAboutToDelay = !isCancelled && !isDelivered && slaStatus === SLA_STATUS.WARNING_48H;

  const focusIconColor = isFocused ? nudosBlue : bordColorTheme.primary.fgDefault;
  const slaDate = slaDeadline ? formatShortDate(slaDeadline)?.onlyDate : 'Sin fecha';
  const hasMoreShipments = shipmentsInCardCount > MAX_VISIBLE_SHIPMENTS;
  const hasTooManyShipments = shipmentsInCardCount > MAX_EXPANDABLE_SHIPMENTS;
  const visibleShipmentGroups = isShipmentsExpanded
    ? shipmentGroups
    : shipmentGroups.slice(0, MAX_VISIBLE_SHIPMENTS);

  const hasDrawerOpen = isShipmentDrawerOpen || isPreparationDrawerOpen;

  const cardClassNames = [
    'operationsCard',
    isAboutToDelay && 'aboutToDelay',
    (isDelayed || isCancelled) && 'delayed',
    isFocused && 'focused',
    hasDrawerOpen && 'drawerOpen'
  ]
    .filter(Boolean)
    .join(' ');

  const orderDetailUrl = orderId ? `/order/details/${orderId}` : '#';

  const handleToggleShipments = () => setIsShipmentsExpanded(prev => !prev);

  const handleFocusOrder = () => {
    const newFocusedOrderId = isFocused ? null : orderId;
    onFocusOrder?.(newFocusedOrderId ?? null);
  };

  const handleCountryClick = () => {
    if (!countryId) return;
    const newFocusedCountryId = isCountryFocused ? null : countryId;
    onCountryClick?.(newFocusedCountryId, countryName);
  };

  const handleOrganizationClick = () => {
    const newFocusedOrganizationName = isOrganizationFocused ? null : organizationName;
    onOrganizationClick?.(newFocusedOrganizationName ?? null);
  };

  const handleShipmentClick = (shipmentIds: number[], index: number) => {
    setSelectedShipmentIds(shipmentIds);
    setSelectedShipmentIndex(index);
    setIsShipmentDrawerOpen(true);
  };

  const handleCloseShipmentDrawer = () => {
    setIsShipmentDrawerOpen(false);
    setSelectedShipmentIds([]);
  };

  const handlePreparationClick = (shipmentId: number, index: number) => {
    setSelectedShipmentIds([shipmentId]);
    setSelectedShipmentIndex(index);
    setIsPreparationDrawerOpen(true);
  };

  const handleClosePreparationDrawer = () => {
    setIsPreparationDrawerOpen(false);
    setSelectedShipmentIds([]);
    onRefetch?.();
  };

  const handleChecklistCountersChange = useCallback(
    (payload: { shipmentId: number; total: number; completed: number }) => {
      setLocalChecklistCounters(prev => ({
        ...prev,
        [payload.shipmentId]: {
          total: payload.total,
          completed: payload.completed
        }
      }));
    },
    []
  );

  const selectedShipmentId = selectedShipmentIds[0] ?? null;
  const isPreparationDrawerOpenForShipment = (shipmentId: number) =>
    isPreparationDrawerOpen && selectedShipmentId === shipmentId;

  const visibleShipmentGroupsWithLocalCounters = visibleShipmentGroups.map(
    (group: IShipmentByStatusItem[]) => {
      const representative = group[0];
      const firstShipmentCounters = localChecklistCounters[representative.id];
      const preparationChecklistTotal =
        firstShipmentCounters?.total ?? representative.preparationChecklistTotal ?? 0;
      const preparationChecklistCompleted =
        firstShipmentCounters?.completed ?? representative.preparationChecklistCompleted ?? 0;
      const mergedShipment: IShipmentByStatusItem = {
        ...representative,
        preparationChecklistTotal,
        preparationChecklistCompleted
      };
      return { shipment: mergedShipment, group };
    }
  );

  return {
    // Estados
    isFocused,
    showCommentsModal,
    isShipmentsExpanded,
    isShipmentDrawerOpen,
    isPreparationDrawerOpen,
    selectedShipmentId,
    selectedShipmentIds,
    selectedShipmentIndex,
    isLoadingComments,

    // Valores derivados
    orderNumber,
    companyName,
    countryFlagUrl,
    countryName,
    countryId,
    isCountryFocused,
    countryFocusIconColor,
    isOrganizationFocused,
    slaDate,
    shipmentsCount,
    shipmentsInCardCount,
    orderComments,
    showFocusButton,
    isCancelled,
    isDelivered,
    isDelayed,
    isAboutToDelay,
    focusIconColor,
    hasMoreShipments,
    hasTooManyShipments,
    visibleShipmentGroups: visibleShipmentGroupsWithLocalCounters,
    cardClassNames,
    orderDetailUrl,

    // Handlers
    handleToggleShipments,
    handleFocusOrder,
    handleCountryClick,
    handleOrganizationClick,
    handleOpenOrCloseCommentsModal,
    handleCommentsSubmission,
    handleShipmentClick,
    handleCloseShipmentDrawer,
    handlePreparationClick,
    handleClosePreparationDrawer,
    handleChecklistCountersChange,
    isPreparationDrawerOpenForShipment
  };
};

export default useOperationsCard;
